namespace $ {

	export type $shm_hitalama_benchmarks_archive_meta = {
		id: string
		started: string
		user: string
		userId: string
		computer: string
		browser: string
		version: string
		/** Wall time including setup, reload samples and correctness checks; excludes archive publication. */
		totalMs?: number
		legacy?: boolean
		/** Derived location, not an additional copy of the report. */
		archiveShard?: string
		archiveKey?: string
	}

	/** Public report index; payloads have independent, author-owned permissions. */
	export class $shm_hitalama_benchmarks_archive extends $mol_object {
		private readonly prepared = new Map<string, $shm_hitalama_benchmarks_catalog_run>()
		private readonly preset: $giper_baza_rank_preset = [[null, $giper_baza_rank_read]]
		private readonly shard_preset: $giper_baza_rank_preset = [[null, $giper_baza_rank_post('just')]]
		private readonly destinations = new Map<string, string>()
		private readonly segments = new Map<string, {key: string, size: number, bytes: number}>()
		private readonly shards = new Map<string, $shm_hitalama_benchmarks_archive_shard>()
		private readonly payloads = new Map<string, $giper_baza_land>()
		static readonly shard_size = 256
		/** Logical UTF-8 payload budget, not a bound on signed CRDT history bytes. */
		static readonly shard_bytes = 1024 * 1024

		/** A session owns its allocation: concurrent tabs never overfill a shared segment. */
		@ $mol_action
		allocate_destination(id: string, day: string, bytes = 0) {
			const saved = this.destinations.get(id)
			if (saved) return saved
			if (!Number.isSafeInteger(bytes) || bytes < 0) throw Error('Некорректный размер результатов')
			let segment = this.segments.get(day)
			if (!segment || segment.size >= $shm_hitalama_benchmarks_archive.shard_size
				|| segment.bytes + bytes > $shm_hitalama_benchmarks_archive.shard_bytes) {
				segment = {key: `shard:${day}:${crypto.randomUUID()}`, size: 0, bytes: 0}
				this.segments.set(day, segment)
			}
			++segment.size
			segment.bytes += bytes
			this.destinations.set(id, segment.key)
			return segment.key
		}

		@ $mol_mem_key
		shard_draft(key: string) {
			$mol_wire_solid()
			let shard = this.shards.get(key)
			if (!shard) {
				shard = $mol_wire_sync(this.$.$giper_baza_glob).land_grab(this.shard_preset).Data($shm_hitalama_benchmarks_archive_shard)
				this.shards.set(key, shard)
			}
			shard.units()
			return shard
		}

		/** Only this author can write the bounded report payload shard. */
		@ $mol_mem_key
		payload_land(key: string) {
			$mol_wire_solid()
			let land = this.payloads.get(key)
			if (!land) {
				land = $mol_wire_sync(this.$.$giper_baza_glob).land_grab(this.preset)
				this.payloads.set(key, land)
			}
			return land
		}

		@ $mol_mem_key
		shard(key: string) {
			return this.catalog()?.key(key)?.remote()?.cast($shm_hitalama_benchmarks_archive_shard) ?? null
		}

		@ $mol_mem
		catalog() {
			return this.$.$shm_hitalama_domain.instance().Benchmark_catalog()?.remote() ?? null
		}

		@ $mol_mem_key
		draft(id: string) {
			$mol_wire_solid()
			let draft = this.prepared.get(id)
			if (!draft) {
				// Reports must not inherit the public POST permission of the index:
				// everyone adds their own report, only its owner edits its payload.
				const destination = this.destinations.get(id)
				const land = destination ? this.payload_land(destination)
					: $mol_wire_sync(this.$.$giper_baza_glob).land_grab(this.preset)
				draft = destination
					? land.Pawn($shm_hitalama_benchmarks_catalog_run).Head($mol_wire_sync(land).self_make())
					: land.Data($shm_hitalama_benchmarks_catalog_run)
				this.prepared.set(id, draft)
			}
			draft.units()
			return draft
		}

		/** Explicit administrator command, never run from rendering. */
		@ $mol_action
		static provision(domain: $shm_hitalama_domain) {
			domain.units()
			if (domain.Benchmark_catalog()?.remote()) return domain.Benchmark_catalog()!.remote()!
			if (!domain.can_change()) throw Error('Создать каталог бенчмарка может только администратор домена')
			const land = domain.land().area_make(0xB3AC4B)
			land.give(null, $giper_baza_rank_post('just'))
			const catalog = land.Data($shm_hitalama_benchmarks_archive_catalog)
			domain.Benchmark_catalog(null)!.remote(catalog)
			return catalog
		}

		@ $mol_action
		save(meta: $shm_hitalama_benchmarks_archive_meta, rows: readonly any[]) {
			const catalog = this.catalog()
			if (!catalog) return false // The domain pointer may still be arriving.
			catalog.units()
			if (!catalog.can_change()) {
				throw Error('Нет прав на запись результатов в каталог бенчмарка')
			}
			if (!meta.id || meta.id.length > 200 || rows.length > 1000 || !Number.isFinite(Date.parse(meta.started))) throw Error('Некорректные параметры прогона')
			const started = new Date(meta.started).toISOString()
			// Check the dated indexes, not every payload in the archive, on replay/F5.
			if (catalog.key(meta.id)?.remote()) return true
			const entryKey = `${started}/${meta.id}`
			for (const key of catalog.keys()) {
				if (typeof key === 'string' && key.startsWith(`shard:${started.slice(0, 10)}:`)
					&& this.shard(key)?.Index()?.key(entryKey)?.remote()) return true
			}
			const values = rows.map(row=> ({
				count: row.count, properties: row.properties, storage: row.storage,
				packing: row.packing, status: row.status, phase: row.phase,
				measurements: row.measurements ?? {}, metricLimits: row.metricLimits ?? {},
				error: String(row.error ?? '').slice(0, 1000),
			}))
			const metaValue = {...meta, started, format: 1, rows: values.length}
			const bytes = $mol_charset_encode(JSON.stringify({meta: metaValue, rows: values})).length
			const shardKey = this.allocate_destination(meta.id, started.slice(0, 10), bytes)
			const shard = this.shard_draft(shardKey)
			const draft = this.draft(meta.id)
			for (let index = 0; index < values.length; ++index) {
				draft.Rows(null)!.key(String(index), null)!.val(values[index])
			}
			draft.Meta(null)!.val(metaValue)
			// Publish only a fully populated report. Replays reuse the same draft.
			shard.Counts(null)!.key(entryKey, null)!.val({rows: values.length})
			shard.Index(null)!.key(entryKey, null)!.remote(draft)
			shard.Meta(null)!.val({format: 2})
			catalog.key(shardKey, null)!.remote(shard)
			return true
		}

		/** Lookup for commands/compatibility. Rendering uses its already known location. */
		reference(id: string, day?: string) {
			const catalog = this.catalog()
			if (catalog?.key(id)?.remote()) return ['', id] as const
			for (const key of catalog?.keys() ?? []) {
				if (typeof key !== 'string' || !key.startsWith(day ? `shard:${day}:` : 'shard:')) continue
				const entry = this.shard(key)?.Index()?.keys().find(entry=> typeof entry === 'string' && entry.endsWith(`/${id}`))
				if (typeof entry === 'string') return [key, entry] as const
			}
			return null
		}

		@ $mol_mem
		runs() {
			return this.page([0, Number.MAX_SAFE_INTEGER]).runs
		}

		/** Stabilize completed cold reads; a large export must not restart per payload. */
		@ $mol_mem_key
		read_batch(locations: readonly (readonly [string, string])[]) {
			return this.$.$mol_wire_race(...locations.map(location=> ()=>
				this.$.$mol_error_fence(()=> this.read_from(location), ()=> null)))
		}

		/** Index work is independent of each pending payload in a cold export. */
		@ $mol_mem_key
		day_references(day: string) {
			const references: {started: string, location: readonly [string, string]}[] = []
			for (const key of this.catalog()?.keys() ?? []) {
				if (typeof key !== 'string' || !key.startsWith(`shard:${day}:`)) continue
				const shard = this.shard(key)
				for (const entry of shard?.Index()?.keys() ?? []) {
					if (typeof entry !== 'string' || entry.length < 26 || entry.slice(0, 10) !== day || entry[24] !== '/') continue
					if (shard?.Deleted()?.key(`run:${entry.slice(25)}`)?.val()) continue
					references.push({started: entry.slice(0, 24), location: [key, entry]})
				}
			}
			return references
		}

		/** Pagination reads the small indexes, not all report payloads. */
		@ $mol_mem
		row_references() {
			const references: {started: string, location: readonly [string, string]}[] = []
			const days = new Set<string>()
			for (const key of this.catalog()?.keys() ?? []) {
				if (typeof key !== 'string' || key.startsWith('deleted-row:')) continue
				const day = /^shard:(\d{4}-\d{2}-\d{2}):/.exec(key)?.[1]
				if (day) days.add(day)
				else {
					const link = this.report_link(['', key])
					const meta = link ? this.read_meta(link) : null
					if (meta?.id === key) references.push({started: meta.started, location: ['', key]})
				}
			}
			for (const day of days) references.push(...this.day_references(day))
			return references.sort((a, b)=> b.started.localeCompare(a.started) || a.location[1].localeCompare(b.location[1]))
		}

		@ $mol_mem_key
		row_summary(location: readonly [string, string]) {
			const [shard, key] = location
			const indexed = shard && this.shard(shard)?.Counts()?.key(key)?.val()?.rows
			if (Number.isInteger(indexed) && Number(indexed) >= 0 && Number(indexed) <= 1000) return Number(indexed)
			// Compatibility reads only old metadata; the explicit initializer below
			// persists a small count so following clients do not unpack old payloads.
			const link = this.report_link(location)
			const meta = link && this.read_meta(link)
			return meta && (shard ? key === `${meta.started}/${meta.id}` : key === meta.id) ? meta.rows : null
		}

		/** Idempotent upgrade of existing shared shard indexes, outside getters. */
		@ $mol_action
		index_counts_ensure() {
			for (const {location} of this.row_references()) {
				if (!location[0]) continue
				const shard = this.shard(location[0])!
				const indexed = shard.Counts()?.key(location[1])?.val()?.rows
				if (Number.isInteger(indexed) && Number(indexed)>=0 && Number(indexed)<=1000 || !shard.can_change()) continue
				const rows = this.row_summary(location)
				if (rows === null) continue // A remote payload may not have arrived yet.
				shard.Counts(null)!.key(location[1], null)!.val({rows})
			}
		}

		@ $mol_mem
		row_index() {
			const seen = new Set<string>()
			return this.row_references().flatMap(({started, location})=> {
				const id = location[0] ? location[1].slice(25) : location[1]
				if (seen.has(id)) return []
				seen.add(id)
				const size = this.row_summary(location) ?? 0
				const deleted = [...new Set([
					...(this.row_deletions(location[0]).get(id) ?? []),
					...(location[0] ? this.row_deletions('').get(id) ?? [] : []),
				])].filter(index=> index<size)
				return [{id, started, location, size, deleted, count: size-deleted.length}]
			})
		}

		/** Scan actual tombstones once per index shard, not once per possible row. */
		@ $mol_mem_key
		row_deletions(shard: string) {
			const deleted = new Map<string,number[]>()
			const keys = shard ? this.shard(shard)?.Deleted()?.keys() ?? [] : this.catalog()?.keys() ?? []
			for (const key of keys) {
				if (typeof key !== 'string' || !shard && !key.startsWith('deleted-row:')) continue
				const match = /^(.*)\/(\d+)$/.exec(shard ? key : key.slice(12))
				if (!match || !Number.isSafeInteger(Number(match[2]))) continue
				const removed = shard ? this.shard(shard)?.Deleted()?.key(key)?.val()
					: this.catalog()?.key(key)?.remote()?.Meta()?.val()?.deleted === true
				if (!removed) continue
				const indices = deleted.get(match[1]) ?? []
				indices.push(Number(match[2])); deleted.set(match[1],indices)
			}
			return deleted
		}

		/** Only indexes of necessary UTC days and payloads of this page are read. */
		@ $mol_mem_key
		page(range: readonly [number, number]) {
			const [offset, limit] = range
			if (!Number.isSafeInteger(offset) || offset < 0 || !Number.isSafeInteger(limit) || limit < 1) throw Error('Некорректная страница результатов')
			const catalog = this.catalog()
			const runs: {meta: $shm_hitalama_benchmarks_archive_meta, rows: any[]}[] = []
			if (!catalog) return {runs, more: false}
			const days = new Map<string, string[]>()
			let references: {started: string, location: readonly [string, string]}[] = []
			for (const key of catalog.keys()) {
				if (typeof key !== 'string' || key.startsWith('deleted-row:')) continue
				const match = /^shard:(\d{4}-\d{2}-\d{2}):/.exec(key)
				if (match) {
					const group = days.get(match[1]) ?? []
					group.push(key); days.set(match[1], group)
				} else {
					// Legacy payloads have no ordered index; retain them without rewriting.
					const run = this.$.$mol_error_fence(()=> this.read_from(['', key]), ()=> null)
					if (run) references.push({started: run.meta.started, location: ['', key]})
				}
			}
			const orderedDays = [...days.keys()].sort().reverse()
			const seen = new Set<string>()
			let skipped = 0
			const append = (items: typeof references)=> {
				items.sort((a, b)=> b.started.localeCompare(a.started) || a.location[1].localeCompare(b.location[1]))
				for (let from = 0; from < items.length;) {
					// Read at most the number still needed, plus one lookahead report.
					const count = Math.min(32, Math.max(1, offset + limit + 1 - skipped))
					const chunk = items.slice(from, from + count)
					from += chunk.length
					for (const run of this.read_batch(chunk.map(item=> item.location))) {
						if (!run || seen.has(run.meta.id)) continue
						seen.add(run.meta.id)
						if (!run.rows.some((_, index)=> !this.row_deleted(run.meta.id, index, run.meta.archiveShard))) continue
						if (skipped++ < offset) continue
						runs.push(run)
						if (runs.length > limit) return true
					}
				}
				return false
			}
			for (const day of orderedDays) {
				// First consume legacy/new references strictly newer than this day.
				const newer = references.filter(item=> item.started.slice(0, 10) > day)
				if (append(newer)) return {runs: runs.slice(0, limit), more: true}
				references = references.filter(item=> item.started.slice(0, 10) <= day)
				references.push(...this.day_references(day))
				const current = references.filter(item=> item.started.slice(0, 10) >= day)
				if (append(current)) return {runs: runs.slice(0, limit), more: true}
				references = references.filter(item=> item.started.slice(0, 10) < day)
			}
			const more = append(references)
			return {runs: runs.slice(0, limit), more}
		}

		row_deleted(id: string, index: number, shard?: string) {
			const location = shard === undefined ? this.reference(id) : [shard, '']
			return !!(location?.[0] && this.shard(location[0])?.Deleted()?.key(`${id}/${index}`)?.val())
				|| this.catalog()?.key(`deleted-row:${id}/${index}`)?.remote()?.Meta()?.val()?.deleted === true
		}

		/** Keep a shared tombstone so an older client cannot resurrect the row. */
		@ $mol_action
		row_delete(id: string, index: number, shard?: string) {
			const catalog = this.catalog()
			if (!catalog?.can_change()) throw Error('Нет прав на удаление результата')
			const entry = shard ? this.shard(shard)?.Index()?.keys().find(key=> typeof key === 'string' && key.endsWith(`/${id}`)) : null
			const location = shard !== undefined ? [shard, typeof entry === 'string' ? entry : id] as const : this.reference(id)
			const run = location && this.read_from(location)
			if (!run || !Number.isInteger(index) || index < 0 || index >= run.rows.length) throw Error('Результат недоступен. Дождитесь загрузки таблицы и повторите удаление')
			const key = `deleted-row:${id}/${index}`
			if (this.row_deleted(id, index, shard ?? run.meta.archiveShard)) return
			if (location![0]) {
				const target = this.shard(location![0])!
				if (!target.can_change()) throw Error('Нет прав на удаление результата')
				target.Deleted(null)!.key(`${id}/${index}`, null)!.val(true)
				if (run.rows.every((_, row)=> this.row_deleted(id, row, location![0]))) {
					target.Deleted(null)!.key(`run:${id}`, null)!.val(true)
				}
				return
			}
			const marker = this.draft(key)
			marker.Meta(null)!.val({format: 0, deleted: true})
			catalog.key(key, null)!.remote(marker)
		}

		read(key: $giper_baza_vary_type) {
			if (typeof key !== 'string') return null
			const location = this.reference(key)
			return location && this.read_from(location)
		}

		@ $mol_mem_key
		report_link(location: readonly [string, string]) {
			const [shard, key] = location
			const run = shard ? this.shard(shard)?.Index()?.key(key)?.remote() : this.catalog()?.key(key)?.remote()
			return run?.link().str ?? null
		}

		/** Cold payload suspensions must not replay the dictionary lookup in the index. */
		@ $mol_mem_key
		read_meta(link: string) {
			const run = this.$.$giper_baza_glob.Pawn(new this.$.$giper_baza_link(link), $shm_hitalama_benchmarks_catalog_run)
			const meta = run.Meta()?.val() as ($shm_hitalama_benchmarks_archive_meta & {format: number, rows: number}) | undefined
			if (!meta || meta.format !== 1 || !Number.isInteger(meta.rows) || meta.rows < 0 || meta.rows > 1000) return null
			if (![meta.id, meta.started, meta.user, meta.userId, meta.computer, meta.browser, meta.version].every(value=> typeof value === 'string')) return null
			if (meta.computer.length > 8000 || meta.browser.length > 4000 || meta.user.length > 1000 || meta.id.length > 200) return null
			return meta
		}

		@ $mol_mem_key
		read_payload(link: string) {
			const meta = this.read_meta(link)
			if (!meta) return null
			const run = this.$.$giper_baza_glob.Pawn(new this.$.$giper_baza_link(link), $shm_hitalama_benchmarks_catalog_run)
			const rows = Array.from({length: meta.rows}, (_, index)=> run.Rows()?.key(String(index))?.val())
			if (rows.some(row=> !row || typeof row.measurements !== 'object' || !row.measurements)) return null
			return {meta, rows}
		}

		@ $mol_mem_key
		read_from(location: readonly [string, string]) {
			const [shard, key] = location
			const link = this.report_link(location)
			const value = link && this.read_payload(link)
			if (!value) return null
			const {meta, rows} = value
			if (shard ? key !== `${meta.started}/${meta.id}` : key !== meta.id) return null
			return {meta: {...meta, archiveShard: shard, archiveKey: key}, rows}
		}

		static browser(navigator: Navigator) {
			const data = (navigator as Navigator & {userAgentData?: {brands: {brand: string, version: string}[], platform: string}}).userAgentData
			const brands = data?.brands.filter(item=> !/not.*brand/i.test(item.brand))
			return brands?.length
				? `${brands.map(item=> `${item.brand} ${item.version}`).join(', ')} (${data!.platform})\n${navigator.userAgent}`
				: navigator.userAgent
		}
	}
}
